import { isValidElement, type ReactNode } from "react";
import type { LucideIcon } from "lucide-react";

/**
 * The one empty state.
 *
 * Five things used to render "this list has nothing in it": AntD's bare
 * `<Empty>` (14 files), a `locale.emptyText` node (16), hand-written markup
 * (16), `LogisticsEmpty` (6) and `ProviderEmptyState` (2). The first two say
 * almost nothing — a grey glyph and one line — which is fine for "no rows" and
 * useless when the vendor doesn't yet know what the screen is for. The last two
 * were already the right idea, built twice.
 *
 * Two variants, because the two originals differed in exactly one way:
 *
 *   inline  no border, for a table's `locale.emptyText` slot — the table
 *           already draws the box (this was LogisticsEmpty)
 *   card    bordered surface, for a standalone panel with nothing around it
 *           (this was ProviderEmptyState)
 *
 * `icon` takes either a lucide component (`icon={Package}`) or a rendered node,
 * since the two originals disagreed about that too.
 */
export interface EmptyStateProps {
  /** A lucide icon component, or any rendered node. */
  icon?: LucideIcon | ReactNode;
  /** Short and scannable — "No shipments yet". */
  title: string;
  /**
   * One or two sentences on why it's empty and what will fill it. Operational,
   * not promotional. Optional: a table that simply has no rows yet doesn't
   * always need a paragraph.
   */
  description?: string;
  /** Optional primary action — "Add your first product". */
  action?: ReactNode;
  variant?: "inline" | "card";
  className?: string;
  "data-cy"?: string;
}

export default function EmptyState({
  icon,
  title,
  description,
  action,
  variant = "inline",
  className = "",
  "data-cy": dataCy = "empty-state",
}: EmptyStateProps) {
  // A lucide icon arrives as a component and needs rendering; anything else is
  // already a node.
  const renderedIcon =
    icon && !isValidElement(icon)
      ? (() => {
          const Icon = icon as LucideIcon;
          return <Icon className="h-5 w-5" style={{ color: "var(--brand-primary)" }} />;
        })()
      : icon;

  const shell =
    variant === "card"
      ? "rounded-xl border border-default bg-surface-elevated p-6 sm:p-8"
      : "px-6 py-12";

  return (
    <div
      data-cy={dataCy}
      className={`flex flex-col items-center justify-center text-center ${shell} ${className}`}
    >
      {renderedIcon && (
        <div
          className="mb-4 flex h-12 w-12 items-center justify-center rounded-full"
          style={{ background: "var(--brand-primary-soft)" }}
        >
          {renderedIcon}
        </div>
      )}
      <p className="m-0 text-sm font-semibold text-primary">{title}</p>
      {description && (
        <p className="m-0 mt-1.5 max-w-sm text-xs leading-relaxed text-tertiary">
          {description}
        </p>
      )}
      {action && <div className="mt-4">{action}</div>}
    </div>
  );
}
